import React, { useState } from "react";
import { Button, FormGroup, Input, Label } from "reactstrap";
import { PUBLIC_DISPLAY_FIELD_OPTIONS, PURCHASE_CURRENCIES } from "../../helpers/collectorUpload";
import { CollectorMastersCatalog, MasterOption, selectState } from "../../helpers/collectorMasters";
import CollectorAddOnFields from "./CollectorAddOnFields";
import McUploadProgress from "./McUploadProgress";
import { CollectorPieceValues } from "./collectorPieceForm";
import { HKPImage } from "../../media/HKPImage";

export type { CollectorPieceValues, CollectorPurchaseGeo, CollectorRegulatoryDoc } from "./collectorPieceForm";
export {
  emptyCollectorPieceValues,
  collectorPieceFromItem,
  collectorJournalBody,
} from "./collectorPieceForm";

type Props = {
  values: CollectorPieceValues;
  masters: CollectorMastersCatalog;
  mode: "add" | "edit";
  disabled?: boolean;
  uploading?: boolean;
  coverUploadPercent?: number | null;
  coverPreview?: string;
  savedCoverUrl?: string;
  fieldErrors?: Record<string, string>;
  showPublicFields?: boolean;
  onChange: (next: CollectorPieceValues) => void;
  /** Change from the latest values (uploads finish after the person may have typed more). */
  onUpdate?: (update: (prev: CollectorPieceValues) => CollectorPieceValues) => void;
  onCoverFile: (file: File) => void;
  onMediaError?: (message: string) => void;
  onMediaBusyChange?: (busy: boolean) => void;
};

const MasterSelect: React.FC<{
  id: string;
  label: string;
  value: string;
  options: MasterOption[];
  disabled?: boolean;
  error?: string;
  hint?: string;
  onChange: (value: string) => void;
}> = ({ id, label, value, options, disabled, error, hint, onChange }) => {
  const state = selectState(value, options);
  return (
    <FormGroup className="mc-field">
      <Label for={id}>{label}</Label>
      <div className={`mc-master-row${state.select === "other" ? " is-other" : ""}`}>
        <Input
          id={id}
          type="select"
          value={state.select}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "other") {
              onChange(state.otherText || "other");
              return;
            }
            onChange(next);
          }}
        >
          <option value="">Select…</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Input>
        {state.select === "other" && (
          <Input
            placeholder="Type the value"
            value={state.otherText}
            disabled={disabled}
            aria-label={`${label} other`}
            onChange={(e) => onChange(e.target.value || "other")}
          />
        )}
      </div>
      {hint && <small className="text-muted d-block mt-1">{hint}</small>}
      {error && <div className="mc-field-error">{error}</div>}
    </FormGroup>
  );
};

const CollectorPieceFields: React.FC<Props> = ({
  values,
  masters,
  mode,
  disabled,
  uploading,
  coverUploadPercent,
  coverPreview,
  savedCoverUrl = "",
  fieldErrors = {},
  showPublicFields = true,
  onChange,
  onUpdate,
  onCoverFile,
  onMediaError,
  onMediaBusyChange,
}) => {
  const set = (patch: Partial<CollectorPieceValues>) => onChange({ ...values, ...patch });
  const preview = coverPreview || values.coverPhotoUrl;
  const coverUnsaved = Boolean(coverPreview) || Boolean(values.coverPhotoUrl && values.coverPhotoUrl !== savedCoverUrl);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState("");

  const captureGeo = () => {
    if (disabled) return;
    if (!navigator.geolocation) {
      setGeoError("This browser cannot share location.");
      return;
    }
    setGeoBusy(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const now = new Date().toISOString();
        set({
          purchaseGeo: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            capturedAt: now,
            consentAt: now,
          },
        });
        setGeoBusy(false);
      },
      (err) => {
        setGeoBusy(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError("Location permission was denied. You can still type the place above.");
        } else {
          setGeoError("Could not read your location. Try again, or type the place above.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const geoMapLink = values.purchaseGeo
    ? `https://www.openstreetmap.org/?mlat=${values.purchaseGeo.lat}&mlon=${values.purchaseGeo.lng}#map=16/${values.purchaseGeo.lat}/${values.purchaseGeo.lng}`
    : "";

  return (
    <>
      <section className="mc-card mb-3">
        <h2 className="h6 text-uppercase text-muted mb-3">Required</h2>
        <FormGroup>
          <Label for="mc-title">
            Title {mode === "add" || mode === "edit" ? <span className="mc-required">*</span> : null}
          </Label>
          <Input
            id="mc-title"
            value={values.title}
            disabled={disabled}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="Wedding Banarasi"
          />
          {fieldErrors.title && <div className="mc-field-error">{fieldErrors.title}</div>}
        </FormGroup>
        <FormGroup>
          <Label for="mc-cover">
            Cover photo {mode === "add" ? <span className="mc-required">*</span> : null}
          </Label>
          <p className="small text-muted">One cover photo. Extra pictures go in Additional images (up to 10).</p>
          <label className="mc-dropzone d-block" htmlFor="mc-cover">
            <input
              id="mc-cover"
              type="file"
              accept="image/*"
              disabled={disabled || uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file) onCoverFile(file);
              }}
            />
            {preview ? (
              <span className="mc-cover-dropzone-current">
                <span className={`mc-cover-preview${uploading ? " is-uploading" : ""}`}>
                  <HKPImage allowMaster={false} preset="PRODUCT_CARD" localPreviewUrl={coverPreview} legacyUrl={values.coverPhotoUrl} alt="Cover photo" />
                  {uploading && (
                    <div className="mc-cover-preview-overlay">
                      <McUploadProgress
                        percent={coverUploadPercent ?? 0}
                        label={`${Math.round(coverUploadPercent ?? 0)}%`}
                      />
                    </div>
                  )}
                </span>
                <span className="d-block mt-2">
                  {uploading
                    ? "Uploading cover photo…"
                    : coverUnsaved
                      ? "New cover ready — tap to replace"
                      : "Current cover (saved) — tap to replace"}
                </span>
              </span>
            ) : uploading ? (
              <McUploadProgress
                percent={coverUploadPercent ?? 0}
                label={`Uploading cover photo… ${Math.round(coverUploadPercent ?? 0)}%`}
              />
            ) : (
              <span>Drop a photo or tap to choose. On a phone you can use the camera.</span>
            )}
          </label>
          {fieldErrors.coverPhotoUrl && <div className="mc-field-error">{fieldErrors.coverPhotoUrl}</div>}
        </FormGroup>
      </section>

      <CollectorAddOnFields
        values={values}
        disabled={disabled}
        onChange={onChange}
        onUpdate={onUpdate}
        onError={onMediaError}
        onBusyChange={onMediaBusyChange}
      />

      <section className="mc-card mb-3">
        <h2 className="h6 text-uppercase text-muted mb-3">Optional details</h2>
        <div className="mc-optional-columns">
          <div>
            <FormGroup>
              <Label for="mc-story">Story</Label>
              <Input
                id="mc-story"
                type="textarea"
                rows={3}
                value={values.story}
                disabled={disabled}
                onChange={(e) => set({ story: e.target.value })}
                placeholder="Where you found it, why it matters…"
              />
            </FormGroup>
            <div className="mc-field-grid">
              <MasterSelect
                id="mc-category"
                label="Category"
                value={values.itemCategory}
                options={masters.itemCategories}
                disabled={disabled}
                onChange={(itemCategory) => set({ itemCategory })}
              />
              <MasterSelect
                id="mc-color"
                label="Color"
                value={values.color}
                options={masters.colors}
                disabled={disabled}
                onChange={(color) => set({ color })}
              />
              <MasterSelect
                id="mc-craft"
                label="Craft type"
                value={values.craftType}
                options={masters.craftTypes}
                disabled={disabled}
                onChange={(craftType) => set({ craftType })}
              />
              <MasterSelect
                id="mc-fabric"
                label="Fabric"
                value={values.fabric}
                options={masters.fabrics}
                disabled={disabled}
                onChange={(fabric) => set({ fabric })}
              />
              <MasterSelect
                id="mc-cluster"
                label="Region / cluster"
                value={values.clusterType}
                options={masters.clusters}
                disabled={disabled}
                hint="Place or weaving cluster, not craft name."
                onChange={(clusterType) => set({ clusterType })}
              />
              <MasterSelect
                id="mc-occasion"
                label="Occasion"
                value={values.occasion}
                options={masters.occasions}
                disabled={disabled}
                onChange={(occasion) => set({ occasion })}
              />
              <FormGroup className="mc-field">
                <Label for="mc-acquired-date">Acquired date</Label>
                <Input
                  id="mc-acquired-date"
                  type={!values.acquiredDate || /^\d{4}-\d{2}-\d{2}$/.test(values.acquiredDate) ? "date" : "text"}
                  value={values.acquiredDate}
                  disabled={disabled}
                  onChange={(e) => set({ acquiredDate: e.target.value })}
                />
              </FormGroup>
              <FormGroup className="mc-field">
                <Label for="mc-acquired-place">Acquired at</Label>
                <Input
                  id="mc-acquired-place"
                  value={values.acquiredPlace}
                  disabled={disabled}
                  onChange={(e) => set({ acquiredPlace: e.target.value })}
                  placeholder="Varanasi"
                />
                <small className="text-muted d-block mt-1">Stays private to your account — not craft origin or region.</small>
              </FormGroup>
            </div>
          </div>
          <div>
            <FormGroup className="mc-field">
              <Label for="mc-price">Price of the product bought</Label>
              <div className="mc-price-row">
                <Input
                  id="mc-currency"
                  type="select"
                  className="mc-currency-select"
                  value={values.purchaseCurrency || "INR"}
                  disabled={disabled}
                  onChange={(e) => set({ purchaseCurrency: e.target.value })}
                  aria-label="Currency"
                >
                  {PURCHASE_CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.label}
                    </option>
                  ))}
                  {values.purchaseCurrency
                    && !PURCHASE_CURRENCIES.some((currency) => currency.code === values.purchaseCurrency)
                    && (
                    <option value={values.purchaseCurrency}>{values.purchaseCurrency}</option>
                  )}
                </Input>
                <Input
                  id="mc-price"
                  inputMode="decimal"
                  value={values.purchasePrice}
                  disabled={disabled}
                  onChange={(e) => set({ purchasePrice: e.target.value })}
                  placeholder="Amount"
                />
              </div>
              {fieldErrors.purchasePrice && <div className="mc-field-error">{fieldErrors.purchasePrice}</div>}
              <small className="text-muted">Only you can see this.</small>
            </FormGroup>
            <FormGroup className="mc-field">
              <Label for="mc-maker">Company / brand / artisan</Label>
              <Input
                id="mc-maker"
                value={values.makerName}
                disabled={disabled}
                onChange={(e) => set({ makerName: e.target.value })}
                placeholder="Optional"
              />
            </FormGroup>
            <FormGroup className="mc-field">
              <Label>Geotag where you bought it</Label>
              <p className="small text-muted mb-2">
                Purchase place only — not craft origin. Stays private to your account.
              </p>
              {values.purchaseGeo ? (
                <div className="mc-geo-card">
                  <div className="mc-geo-grid">
                    <div>
                      <span className="mc-geo-label">Latitude</span>
                      <span className="mc-geo-value">{values.purchaseGeo.lat.toFixed(5)}</span>
                    </div>
                    <div>
                      <span className="mc-geo-label">Longitude</span>
                      <span className="mc-geo-value">{values.purchaseGeo.lng.toFixed(5)}</span>
                    </div>
                    {values.purchaseGeo.accuracy != null && (
                      <div className="mc-geo-accuracy">
                        <span className="mc-geo-label">Accuracy</span>
                        <span className="mc-geo-value">about {Math.round(values.purchaseGeo.accuracy)} m</span>
                      </div>
                    )}
                  </div>
                  <div className="mc-geo-actions">
                    <a className="mc-btn-secondary" href={geoMapLink} target="_blank" rel="noreferrer">
                      Open map
                    </a>
                    <Button
                      type="button"
                      className="mc-btn-ghost"
                      disabled={disabled}
                      onClick={() => set({ purchaseGeo: null })}
                    >
                      Clear tag
                    </Button>
                  </div>
                </div>
              ) : (
                <Button type="button" className="mc-btn-secondary" disabled={disabled || geoBusy} onClick={captureGeo}>
                  {geoBusy ? "Finding location…" : "Use my current location"}
                </Button>
              )}
              {geoError && <div className="mc-field-error">{geoError}</div>}
            </FormGroup>
            <FormGroup className="mc-field">
              <Label for="mc-visibility">Visibility</Label>
              <Input
                id="mc-visibility"
                type="select"
                value={values.visibility}
                disabled={disabled}
                onChange={(e) => set({ visibility: e.target.value })}
              >
                <option value="PUBLIC">Public</option>
                <option value="PRIVATE">Private</option>
              </Input>
              <small className="text-muted d-block mt-1">
                {values.visibility === "PRIVATE"
                  ? "Private: a passkey is required to open the public page."
                  : "Public: anyone with the link can see photo, title, and story. Optional fields stay hidden unless you tick them below."}
              </small>
            </FormGroup>
            {showPublicFields && (
              <FormGroup className="mc-field mb-0">
                <Label>Show on public QR page</Label>
                <div className="mc-public-checks">
                  {PUBLIC_DISPLAY_FIELD_OPTIONS.map((opt) => (
                    <label key={opt.key}>
                      <input
                        type="checkbox"
                        checked={values.publicDisplayFields.includes(opt.key)}
                        disabled={disabled}
                        onChange={() => {
                          const has = values.publicDisplayFields.includes(opt.key);
                          set({
                            publicDisplayFields: has
                              ? values.publicDisplayFields.filter((key) => key !== opt.key)
                              : [...values.publicDisplayFields, opt.key],
                          });
                        }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                <small className="text-muted">Photo, title, and story always appear when public. Price, purchase place, geotag, and regulatory docs never do.</small>
              </FormGroup>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default CollectorPieceFields;
